/**
 * Local storage for daily challenge state, streaks, and history.
 * Will be synced to Supabase for logged-in users in v1.1+.
 */

import type { Stage } from "../audio";

export interface DailyResult {
  dateKey: string;
  stage: Stage | 0; // 0 = failed
  score: number;
  guesses: string[]; // guess history for share card
}

export interface PlayerStats {
  currentStreak: number;
  maxStreak: number;
  totalPlayed: number;
  totalScore: number;
  stageDistribution: Record<string, number>; // "1"→count, "2"→count, ..., "0"→failed
}

const RESULTS_KEY = "riffed:results";
const STATS_KEY = "riffed:stats";

// --- Daily Results ---

export function getDailyResult(dateKey: string): DailyResult | null {
  const results = getAllResults();
  return results[dateKey] ?? null;
}

export function saveDailyResult(result: DailyResult): void {
  const results = getAllResults();
  results[result.dateKey] = result;
  localStorage.setItem(RESULTS_KEY, JSON.stringify(results));
  updateStats(results);
}

function getAllResults(): Record<string, DailyResult> {
  try {
    return JSON.parse(localStorage.getItem(RESULTS_KEY) || "{}");
  } catch {
    return {};
  }
}

// --- Stats ---

export function getStats(): PlayerStats {
  try {
    const stored = localStorage.getItem(STATS_KEY);
    if (stored) return JSON.parse(stored);
  } catch { /* ignore */ }

  return {
    currentStreak: 0,
    maxStreak: 0,
    totalPlayed: 0,
    totalScore: 0,
    stageDistribution: {},
  };
}

function updateStats(results: Record<string, DailyResult>): void {
  const dates = Object.keys(results).sort();
  const distribution: Record<string, number> = {};
  let totalScore = 0;

  for (const date of dates) {
    const r = results[date];
    const key = String(r.stage);
    distribution[key] = (distribution[key] || 0) + 1;
    totalScore += r.score;
  }

  // Calculate streak
  let currentStreak = 0;
  let maxStreak = 0;
  let streak = 0;

  for (let i = 0; i < dates.length; i++) {
    const r = results[dates[i]];
    if (r.score > 0) {
      streak++;
      maxStreak = Math.max(maxStreak, streak);
    } else {
      streak = 0;
    }
  }

  // Check if current streak is still active (includes today or yesterday)
  const today = new Date();
  const yesterday = new Date(today);
  yesterday.setDate(yesterday.getDate() - 1);
  const todayKey = formatDate(today);
  const yesterdayKey = formatDate(yesterday);
  const lastDate = dates[dates.length - 1];

  if (lastDate === todayKey || lastDate === yesterdayKey) {
    currentStreak = streak;
  } else {
    currentStreak = 0;
  }

  const stats: PlayerStats = {
    currentStreak,
    maxStreak,
    totalPlayed: dates.length,
    totalScore,
    stageDistribution: distribution,
  };

  localStorage.setItem(STATS_KEY, JSON.stringify(stats));
}

function formatDate(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, "0")}-${String(d.getDate()).padStart(2, "0")}`;
}
